import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc,
  getDocs,
  getDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import { useFamilyId } from './FamilyIdContext';
import { createRecurringTaskInstance, shouldRecreateTask } from '../utils/recurringTaskHelper';

const FamilyContext = createContext();

export function useFamily() {
  return useContext(FamilyContext);
}

export function FamilyProvider({ children }) {
  const [tasks, setTasks] = useState([]);
  const [shoppingList, setShoppingList] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentUser, userProfile } = useAuth();
  const { familyId } = useFamilyId();

  // Fetch family members (MULTI-TENANT: Filter by familyId)
  // Includes both parent users AND children (COPPA-compliant child profiles)
  useEffect(() => {
    if (!currentUser || !familyId) {
      setFamilyMembers([]);
      return;
    }

    const fetchFamilyMembers = async () => {
      try {
        // Check if current user is a child session (no Firebase Auth token)
        // Children have UID prefixed with 'child:' and can't query the users collection
        const isChildSession = currentUser?.uid?.startsWith('child:');

        let users = [];
        if (!isChildSession) {
          // Only fetch parent users when authenticated via Firebase Auth
          // Children have no Firebase Auth token, so this query would fail with
          // "Missing or insufficient permissions"
          const usersQuery = query(
            collection(db, 'users'),
            where('familyId', '==', familyId)
          );
          const usersSnapshot = await getDocs(usersQuery);
          users = usersSnapshot.docs.map(doc => ({
            id: doc.id,
            uid: doc.id,  // Firebase Auth UID for parents
            type: 'user',
            ...doc.data()
          }));
        }
        
        // Fetch children from 'families/{familyId}/children' subcollection
        // (COPPA-compliant: no Firebase Auth, no email — anyone can read)
        const childrenRef = collection(db, 'families', familyId, 'children');
        const childrenSnapshot = await getDocs(childrenRef);
        const childrenProfiles = childrenSnapshot.docs.map(doc => ({
          id: `child:${doc.id}`,  // Prefixed ID for task assignment
          childId: doc.id,  // Original child ID
          uid: `child:${doc.id}`,  // Compatible with task queries
          type: 'child',
          role: 'child',
          displayName: doc.data().firstName,  // Children only have first name
          ...doc.data()
        }));
        
        // Combine both parent users and children
        const allMembers = [...users, ...childrenProfiles];
        setFamilyMembers(allMembers);
        
        console.log(`✅ Loaded ${users.length} parents and ${childrenProfiles.length} children`);
      } catch (error) {
        console.error('Error fetching family members:', error);
      }
    };

    fetchFamilyMembers();
  }, [currentUser, familyId]);

  // Fetch tasks (MULTI-TENANT: Filter by familyId)
  useEffect(() => {
    if (!currentUser || !familyId) {
      setTasks([]);
      setLoading(false);
      return;
    }

    let tasksQuery;
    if (userProfile?.role === 'parent') {
      // Parents see all family tasks
      tasksQuery = query(
        collection(db, 'tasks'),
        where('familyId', '==', familyId),
        orderBy('createdAt', 'desc')
      );
    } else {
      // Children see only their assigned tasks in their family
      tasksQuery = query(
        collection(db, 'tasks'),
        where('familyId', '==', familyId),
        where('assignedTo', 'array-contains', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
    }

    const unsubscribe = onSnapshot(tasksQuery, (snapshot) => {
      const tasksData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTasks(tasksData);
      setLoading(false);
    });

    return unsubscribe;
  }, [currentUser, userProfile, familyId]);

  // Fetch shopping list (MULTI-TENANT: Filter by familyId)
  useEffect(() => {
    if (!currentUser || !familyId) {
      setShoppingList([]);
      return;
    }

    const shoppingQuery = query(
      collection(db, 'shoppingList'),
      where('familyId', '==', familyId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(shoppingQuery, (snapshot) => {
      const shoppingData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setShoppingList(shoppingData);
    });

    return unsubscribe;
  }, [currentUser, familyId]);

  // One-time sync: catch any existing tasks that don't have calendar events yet
  useEffect(() => {
    if (!familyId || !tasks.length || !userProfile?.role) return;
    // Only parents trigger the sync (avoid duplicate syncs from child sessions)
    if (userProfile.role !== 'parent') return;

    const syncOrphanedTasks = async () => {
      try {
        const orphanedTasks = tasks.filter(t =>
          t.status === 'pending' && !t.calendarEventId && t.title
        );
        if (orphanedTasks.length === 0) return;

        console.log(`📅 Syncing ${orphanedTasks.length} orphaned tasks to calendar...`);
        for (const task of orphanedTasks) {
          try {
            const dueDate = task.dueDate ? new Date(task.dueDate) : new Date();
            // Validate date
            if (isNaN(dueDate.getTime())) continue;

            const eventRef = await addDoc(collection(db, 'events'), {
              familyId,
              title: task.title,
              start: dueDate,
              end: new Date(dueDate.getTime() + 60 * 60 * 1000),
              eventType: 'chore',
              assignedTo: Array.isArray(task.assignedTo) ? task.assignedTo : [],
              description: task.description || '',
              status: 'pending',
              relatedChoreId: task.id,
              isRecurring: task.isRecurring || false,
              recurringType: task.recurringType || null,
              createdAt: new Date().toISOString()
            });

            await updateDoc(doc(db, 'tasks', task.id), {
              calendarEventId: eventRef.id
            });
            console.log(`📅 Synced orphaned task to calendar: ${task.title}`);
          } catch (taskErr) {
            console.warn(`⚠️ Failed to sync task ${task.id}:`, taskErr);
          }
        }
      } catch (err) {
        console.error('Error in orphaned task sync:', err);
      }
    };

    syncOrphanedTasks();
  }, [tasks, familyId, userProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  // Task management functions
  const createTask = async (taskData) => {
    if (!familyId) throw new Error('No family selected');
    
    try {
      // Get assigned member names for calendar description
      const assignedToNames = taskData.assignedTo?.map(userId => {
        const member = familyMembers.find(m => m.id === userId);
        return member?.displayName || 'Unknown';
      }) || [];

      const taskWithNames = {
        ...taskData,
        assignedToNames
      };

      const docRef = await addDoc(collection(db, 'tasks'), {
        ...taskWithNames,
        familyId, // MULTI-TENANT: Include familyId
        createdAt: new Date().toISOString(),
        status: 'pending',
        completedAt: null,
        approvedAt: null,
        photos: [],
        favorited: false
      });

      // Auto-create calendar event for ALL chores (use today if no dueDate)
      try {
        const dueDate = taskData.dueDate ? new Date(taskData.dueDate) : new Date();
        // If startTime provided, merge it into the date
        if (taskData.startTime) {
          const [hours, minutes] = taskData.startTime.split(':');
          dueDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
        }
        const endDate = taskData.endTime
          ? (() => { const d = new Date(dueDate); const [h, m] = taskData.endTime.split(':'); d.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0); return d; })()
          : new Date(dueDate.getTime() + 60 * 60 * 1000); // default 1 hour

        const eventRef = await addDoc(collection(db, 'events'), {
          familyId,
          title: taskData.title,
          start: dueDate,
          end: endDate,
          eventType: 'chore',
          assignedTo: taskData.assignedTo || [],
          assignedToNames: assignedToNames,
          description: taskData.description || '',
          status: 'pending',
          relatedChoreId: docRef.id,
          isRecurring: taskData.isRecurring || false,
          recurringType: taskData.recurringType || null,
          createdAt: new Date().toISOString()
        });

        // Update task with calendar event ID
        await updateDoc(doc(db, 'tasks', docRef.id), {
          calendarEventId: eventRef.id
        });

        console.log('📅 Calendar event created for task:', taskData.title);
      } catch (calError) {
        console.error('⚠️ Failed to create calendar event:', calError);
        // Don't fail the task creation if calendar sync fails
      }

      return docRef.id;
    } catch (error) {
      console.error('Error creating task:', error);
      throw error;
    }
  };

  const updateTask = async (taskId, updates) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      
      // Update the task
      await updateDoc(doc(db, 'tasks', taskId), {
        ...updates,
        updatedAt: new Date().toISOString()
      });

      // If task is being approved, update user allowances
      if (updates.status === 'completed' && updates.approvedAt) {
        if (task && task.assignedTo && task.allowance) {
          // Update allowance for assigned CHILDREN only (parents don't earn allowance)
          for (const userId of task.assignedTo) {
            if (userId.startsWith('child:')) {
              const childId = userId.replace('child:', '');
              const childRef = doc(db, 'families', familyId, 'children', childId);
              const childDoc = await getDoc(childRef);
              if (childDoc.exists()) {
                const currentAllowance = childDoc.data().allowance || 0;
                const currentTotalEarned = childDoc.data().totalEarned || 0;
                await updateDoc(childRef, {
                  allowance: currentAllowance + task.allowance,
                  totalEarned: currentTotalEarned + task.allowance,
                  lastTaskCompleted: new Date().toISOString()
                });
                console.log(`💰 Updated child allowance: ${childDoc.data().firstName} +$${task.allowance}`);
              }
            }
            // Skip parent UIDs — parents approve tasks, they don't earn from them
          }
        }

        // Auto-allocate to savings goals if any are configured
        if (task && task.assignedTo && task.allowance) {
          for (const userId of task.assignedTo) {
            try {
              const goalsQuery = query(
                collection(db, 'savingsGoals'),
                where('userId', '==', userId),
                where('status', '==', 'active')
              );
              const goalsSnap = await getDocs(goalsQuery);
              for (const goalDoc of goalsSnap.docs) {
                const goal = goalDoc.data();
                if (goal.autoAllocate && goal.autoAllocatePercent > 0) {
                  const allocAmount = Math.round((task.allowance * goal.autoAllocatePercent / 100) * 100) / 100;
                  if (allocAmount > 0 && (goal.currentAmount || 0) < goal.targetAmount) {
                    const newAmount = Math.min((goal.currentAmount || 0) + allocAmount, goal.targetAmount);
                    const actualAlloc = newAmount - (goal.currentAmount || 0);
                    await updateDoc(doc(db, 'savingsGoals', goalDoc.id), {
                      currentAmount: newAmount
                    });
                    // Deduct auto-allocated amount from child's allowance
                    if (userId.startsWith('child:') && actualAlloc > 0) {
                      const cId = userId.replace('child:', '');
                      const cRef = doc(db, 'families', familyId, 'children', cId);
                      const cDoc = await getDoc(cRef);
                      if (cDoc.exists()) {
                        const curAllow = cDoc.data().allowance || 0;
                        await updateDoc(cRef, {
                          allowance: Math.max(0, curAllow - actualAlloc)
                        });
                        console.log(`💰 Deducted $${actualAlloc} from ${userId} allowance for auto-save`);
                      }
                    }
                    console.log(`🎯 Auto-allocated $${actualAlloc} to goal "${goal.name}" for ${userId}`);
                  }
                }
              }
            } catch (goalErr) {
              console.warn('Auto-allocate to savings goals failed:', goalErr);
              // Don't block task approval if savings allocation fails
            }
          }
        }

        // If this is a recurring task, create a new instance + calendar event
        if (task && task.isRecurring && shouldRecreateTask({ ...task, ...updates })) {
          try {
            const newRecurringTask = createRecurringTaskInstance({ ...task, ...updates });
            // Ensure familyId is carried over
            newRecurringTask.familyId = newRecurringTask.familyId || task.familyId || familyId;
            // Ensure all required fields are present
            if (newRecurringTask.familyId) {
              const recurDocRef = await addDoc(collection(db, 'tasks'), newRecurringTask);
              console.log('Created new recurring task instance');

              // Auto-create calendar event for the new recurring instance
              if (newRecurringTask.dueDate) {
                try {
                  const recurDueDate = new Date(newRecurringTask.dueDate);
                  const recurEventRef = await addDoc(collection(db, 'events'), {
                    familyId: newRecurringTask.familyId,
                    title: newRecurringTask.title,
                    start: recurDueDate,
                    end: new Date(recurDueDate.getTime() + 60 * 60 * 1000),
                    eventType: 'chore',
                    assignedTo: newRecurringTask.assignedTo || [],
                    description: newRecurringTask.description || '',
                    status: 'pending',
                    relatedChoreId: recurDocRef.id,
                    isRecurring: true,
                    recurringType: newRecurringTask.recurringType || null,
                    createdAt: new Date().toISOString()
                  });
                  await updateDoc(doc(db, 'tasks', recurDocRef.id), {
                    calendarEventId: recurEventRef.id
                  });
                  console.log('📅 Calendar event created for recurring task:', newRecurringTask.title);
                } catch (calErr) {
                  console.warn('⚠️ Failed to create calendar event for recurring task:', calErr);
                }
              }
            } else {
              console.warn('Skipping recurring task creation - missing required fields');
            }
          } catch (recurError) {
            console.error('Error creating recurring task instance:', recurError);
            // Don't fail the whole approval if recurring creation fails
          }
        }
      }
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  };

  const deleteTask = async (taskId) => {
    try {
      // Note: Calendar events are now handled by the native calendar system
      console.log('🗑️ Deleting task - calendar events will be synced automatically');
      
      await deleteDoc(doc(db, 'tasks', taskId));
      console.log('✅ Task deleted from Firestore');
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  };

  // Shopping list functions
  const addShoppingItem = async (itemData) => {
    if (!familyId) throw new Error('No family selected');
    
    try {
      const docRef = await addDoc(collection(db, 'shoppingList'), {
        ...itemData,
        familyId, // MULTI-TENANT: Include familyId
        createdAt: new Date().toISOString(),
        status: 'pending',
        approvedAt: null
      });
      return docRef.id;
    } catch (error) {
      console.error('Error adding shopping item:', error);
      throw error;
    }
  };

  const updateShoppingItem = async (itemId, updates) => {
    try {
      await updateDoc(doc(db, 'shoppingList', itemId), {
        ...updates,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error updating shopping item:', error);
      throw error;
    }
  };

  const deleteShoppingItem = async (itemId) => {
    try {
      await deleteDoc(doc(db, 'shoppingList', itemId));
    } catch (error) {
      console.error('Error deleting shopping item:', error);
      throw error;
    }
  };

  const value = {
    tasks,
    shoppingList,
    familyMembers,
    loading,
    createTask,
    updateTask,
    deleteTask,
    addShoppingItem,
    updateShoppingItem,
    deleteShoppingItem
  };

  return (
    <FamilyContext.Provider value={value}>
      {children}
    </FamilyContext.Provider>
  );
}
